import React, { useState, useEffect } from 'react';
import { Navigation, AlertTriangle, Users, ArrowRight, RotateCcw, Play, Pause, Save, Plus, X } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface Zone {
  id: string;
  name: string;
  currentOccupancy: number;
  maxCapacity: number;
  congestionLevel: 'low' | 'medium' | 'high';
  averageDwellTime: number;
  peakHours: string;
  commonPaths: {
    to: string;
    traffic: number;
    congestionPoints: string[];
  }[];
}

interface OptimizationScenario {
  id: string;
  name: string;
  description: string;
  impactScore: number;
  implementationCost: 'low' | 'medium' | 'high';
  estimatedSavings: number;
  affectedZones: string[];
}

interface ScenarioFormData {
  name: string;
  description: string;
  affectedZones: string[];
  proposedChanges: {
    zoneId: string;
    change: 'routing' | 'capacity' | 'timing';
    details: string;
  }[];
  implementationCost: 'low' | 'medium' | 'high';
}

const TrafficFlowManager: React.FC = () => {
  // State Management
  const [zones, setZones] = useState<Zone[]>([
    {
      id: 'z1',
      name: 'Main Entrance',
      currentOccupancy: 45,
      maxCapacity: 100,
      congestionLevel: 'high',
      averageDwellTime: 2.5,
      peakHours: '8:30 AM - 9:30 AM',
      commonPaths: [
        {
          to: 'Elevator Lobby',
          traffic: 250,
          congestionPoints: ['Security Gates', 'Information Desk']
        },
        {
          to: 'Cafeteria',
          traffic: 150,
          congestionPoints: ['Corridor A']
        }
      ]
    },
    {
      id: 'z2',
      name: 'Elevator Lobby',
      currentOccupancy: 15,
      maxCapacity: 40,
      congestionLevel: 'medium',
      averageDwellTime: 1.8,
      peakHours: '8:45 AM - 9:15 AM',
      commonPaths: [
        {
          to: 'Office Area North',
          traffic: 180,
          congestionPoints: ['Elevator Bank']
        },
        {
          to: 'Conference Center',
          traffic: 120,
          congestionPoints: ['Corridor B']
        }
      ]
    }
  ]);

  const [scenarios, setScenarios] = useState<OptimizationScenario[]>([
    {
      id: 's1',
      name: 'Entrance Flow Optimization',
      description: 'Reconfigure security gates and add express lane for pass holders',
      impactScore: 8.5,
      implementationCost: 'medium',
      estimatedSavings: 12500,
      affectedZones: ['Main Entrance', 'Security Checkpoint']
    },
    {
      id: 's2',
      name: 'Elevator Bank Rebalancing',
      description: 'Optimize elevator algorithms based on traffic patterns',
      impactScore: 7.2,
      implementationCost: 'low',
      estimatedSavings: 8000,
      affectedZones: ['Elevator Lobby', 'Office Area North']
    }
  ]);

  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [showScenarioForm, setShowScenarioForm] = useState(false);

  // Simulation data
  const [flowData, setFlowData] = useState<any[]>([]);

  const [scenarioForm, setScenarioForm] = useState<ScenarioFormData>({
    name: '',
    description: '',
    affectedZones: [],
    proposedChanges: [],
    implementationCost: 'medium'
  });

  const [simulationResults, setSimulationResults] = useState<{
    impactScore: number;
    estimatedSavings: number;
    congestionReduction: number;
  } | null>(null);

  // Generate simulated flow data
  useEffect(() => {
    if (isSimulating) {
      const interval = setInterval(() => {
        const now = new Date();
        const newDataPoint = {
          time: now.toLocaleTimeString(),
          mainEntrance: Math.floor(Math.random() * 50) + 30,
          elevatorLobby: Math.floor(Math.random() * 25) + 10,
          congestion: Math.random() * 100
        };
        
        setFlowData(prev => [...prev.slice(-20), newDataPoint]);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isSimulating]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center bg-white rounded-lg shadow p-4">
        <h2 className="text-2xl font-bold">Traffic Flow Manager</h2>
        <div className="flex space-x-4">
          <button
            onClick={() => setIsSimulating(!isSimulating)}
            className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            {isSimulating ? (
              <>
                <Pause className="w-5 h-5" />
                <span>Pause Simulation</span>
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                <span>Start Simulation</span>
              </>
            )}
          </button>
          <button
            onClick={() => setShowScenarioForm(true)}
            className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            <Save className="w-5 h-5" />
            <span>New Scenario</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Real-time Flow Analysis */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Real-time Flow Analysis</h3>
          <div className="space-y-4">
            {zones.map(zone => (
              <div
                key={zone.id}
                className={`border rounded-lg p-4 cursor-pointer ${
                  selectedZone === zone.id ? 'ring-2 ring-blue-500' : ''
                }`}
                onClick={() => setSelectedZone(zone.id)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">{zone.name}</h4>
                    <div className="text-sm text-gray-500">
                      Current Occupancy: {zone.currentOccupancy}/{zone.maxCapacity}
                    </div>
                    <div className="text-sm">
                      Peak Hours: {zone.peakHours}
                    </div>
                    <div className="text-sm">
                      Avg Dwell Time: {zone.averageDwellTime} min
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className={`px-2 py-1 rounded text-sm ${
                      zone.congestionLevel === 'high' ? 'bg-red-100 text-red-800' :
                      zone.congestionLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {zone.congestionLevel} congestion
                    </span>
                  </div>
                </div>
                {selectedZone === zone.id && (
                  <div className="mt-4 space-y-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h5 className="font-medium mb-2">Common Paths</h5>
                      {zone.commonPaths.map((path, index) => (
                        <div key={index} className="mb-2">
                          <div className="flex items-center text-sm">
                            <span>{zone.name}</span>
                            <ArrowRight className="w-4 h-4 mx-2" />
                            <span>{path.to}</span>
                            <span className="ml-2 text-blue-600">
                              ({path.traffic} people/hour)
                            </span>
                          </div>
                          {path.congestionPoints.length > 0 && (
                            <div className="ml-6 text-sm text-red-600">
                              Congestion points: {path.congestionPoints.join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Optimization Scenarios */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Optimization Scenarios</h3>
          <div className="space-y-4">
            {scenarios.map(scenario => (
              <div key={scenario.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">{scenario.name}</h4>
                    <div className="text-sm text-gray-500">{scenario.description}</div>
                    <div className="text-sm mt-2">
                      Affected Areas: {scenario.affectedZones.join(', ')}
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="text-sm">
                      Impact Score: {scenario.impactScore}/10
                    </div>
                    <div className="text-sm">
                      Cost: {scenario.implementationCost}
                    </div>
                    <div className="text-sm text-green-600">
                      Savings: ${scenario.estimatedSavings.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Real-time Flow Visualization */}
      {isSimulating && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Live Traffic Flow</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={flowData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="mainEntrance" 
                  stroke="#3b82f6" 
                  name="Main Entrance"
                />
                <Line 
                  type="monotone" 
                  dataKey="elevatorLobby" 
                  stroke="#10b981" 
                  name="Elevator Lobby"
                />
                <Line 
                  type="monotone" 
                  dataKey="congestion" 
                  stroke="#ef4444" 
                  name="Congestion Index"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* New Scenario Form */}
      {showScenarioForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-[800px] max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Create New Scenario</h3>
              <button
                onClick={() => setShowScenarioForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Basic Info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scenario Name
                </label>
                <input
                  type="text"
                  value={scenarioForm.name}
                  onChange={(e) => setScenarioForm(prev => ({
                    ...prev,
                    name: e.target.value
                  }))}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="e.g., Morning Rush Hour Optimization"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={scenarioForm.description}
                  onChange={(e) => setScenarioForm(prev => ({
                    ...prev,
                    description: e.target.value
                  }))}
                  className="w-full px-3 py-2 border rounded-md"
                  rows={3}
                  placeholder="Describe the proposed changes and expected outcomes"
                />
              </div>

              {/* Affected Zones */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Affected Zones
                </label>
                <div className="space-y-2">
                  {zones.map(zone => (
                    <label key={zone.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={scenarioForm.affectedZones.includes(zone.id)}
                        onChange={(e) => {
                          const newZones = e.target.checked
                            ? [...scenarioForm.affectedZones, zone.id]
                            : scenarioForm.affectedZones.filter(id => id !== zone.id);
                          setScenarioForm(prev => ({
                            ...prev,
                            affectedZones: newZones
                          }));
                        }}
                        className="rounded border-gray-300"
                      />
                      <span>{zone.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Proposed Changes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Proposed Changes
                </label>
                {scenarioForm.proposedChanges.map((change, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <select
                      value={change.zoneId}
                      onChange={(e) => {
                        const newChanges = [...scenarioForm.proposedChanges];
                        newChanges[index].zoneId = e.target.value;
                        setScenarioForm(prev => ({
                          ...prev,
                          proposedChanges: newChanges
                        }));
                      }}
                      className="px-3 py-2 border rounded-md"
                    >
                      <option value="">Select Zone</option>
                      {zones.map(zone => (
                        <option key={zone.id} value={zone.id}>{zone.name}</option>
                      ))}
                    </select>
                    <select
                      value={change.change}
                      onChange={(e) => {
                        const newChanges = [...scenarioForm.proposedChanges];
                        newChanges[index].change = e.target.value as any;
                        setScenarioForm(prev => ({
                          ...prev,
                          proposedChanges: newChanges
                        }));
                      }}
                      className="px-3 py-2 border rounded-md"
                    >
                      <option value="routing">Path Routing</option>
                      <option value="capacity">Capacity Change</option>
                      <option value="timing">Timing Adjustment</option>
                    </select>
                    <input
                      type="text"
                      value={change.details}
                      onChange={(e) => {
                        const newChanges = [...scenarioForm.proposedChanges];
                        newChanges[index].details = e.target.value;
                        setScenarioForm(prev => ({
                          ...prev,
                          proposedChanges: newChanges
                        }));
                      }}
                      className="flex-1 px-3 py-2 border rounded-md"
                      placeholder="Change details..."
                    />
                    <button
                      onClick={() => {
                        setScenarioForm(prev => ({
                          ...prev,
                          proposedChanges: prev.proposedChanges.filter((_, i) => i !== index)
                        }));
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => {
                    setScenarioForm(prev => ({
                      ...prev,
                      proposedChanges: [
                        ...prev.proposedChanges,
                        { zoneId: '', change: 'routing', details: '' }
                      ]
                    }));
                  }}
                  className="flex items-center space-x-1 text-blue-500 hover:text-blue-700"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Change</span>
                </button>
              </div>

              {/* Implementation Cost */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Implementation Cost
                </label>
                <select
                  value={scenarioForm.implementationCost}
                  onChange={(e) => setScenarioForm(prev => ({
                    ...prev,
                    implementationCost: e.target.value as any
                  }))}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              {/* Simulation Results */}
              {simulationResults && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Simulation Results</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-sm text-gray-500">Impact Score</div>
                      <div className="text-lg font-medium">{simulationResults.impactScore}/10</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Estimated Savings</div>
                      <div className="text-lg font-medium">${simulationResults.estimatedSavings.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Congestion Reduction</div>
                      <div className="text-lg font-medium">{simulationResults.congestionReduction}%</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowScenarioForm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Simulate scenario
                  setSimulationResults({
                    impactScore: 7 + Math.random() * 3,
                    estimatedSavings: Math.floor(5000 + Math.random() * 15000),
                    congestionReduction: Math.floor(10 + Math.random() * 30)
                  });
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Run Simulation
              </button>
              <button
                onClick={() => {
                  // Add new scenario
                  const newScenario: OptimizationScenario = {
                    id: `s${scenarios.length + 1}`,
                    name: scenarioForm.name,
                    description: scenarioForm.description,
                    impactScore: simulationResults?.impactScore || 0,
                    implementationCost: scenarioForm.implementationCost,
                    estimatedSavings: simulationResults?.estimatedSavings || 0,
                    affectedZones: zones
                      .filter(z => scenarioForm.affectedZones.includes(z.id))
                      .map(z => z.name)
                  };
                  setScenarios(prev => [...prev, newScenario]);
                  setShowScenarioForm(false);
                  setScenarioForm({
                    name: '',
                    description: '',
                    affectedZones: [],
                    proposedChanges: [],
                    implementationCost: 'medium'
                  });
                  setSimulationResults(null);
                }}
                disabled={!simulationResults}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Scenario
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrafficFlowManager; 